import React, { useCallback, useState } from 'react';
import { Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Bot,
  Calculator,
  Dna,
  FileText,
  MessageSquare,
  ScanFace,
  Share2,
  Sparkles,
  Zap,
  Loader2,
} from 'lucide-react';
import { useFetchPersonaSnapshotsQuery, useLazyFetchPersonaSnapshotQuery } from '#store';
import { Routes } from '#Routes';

/**
 * Quick actions grid (v0 `quick-actions-card.tsx` equivalent): 2×n tiles, Bootstrap + lucide.
 * Seven navigation actions + “Copy AI profile” (premium: copy when content exists, else open AI profile page).
 */
const ACTIONS = [
  {
    Icon: FileText,
    path: Routes.MedicalDocumentCreate.path,
    labelKey: 'dashboard.add-new-document',
  },
  {
    Icon: Bot,
    path: Routes.MedicalDocumentsList.path,
    labelKey: 'dashboard.create-medical-document-ai-report',
  },
  {
    Icon: Sparkles,
    path: Routes.HealthReportCreate.path,
    labelKey: 'dashboard.create-new-report',
  },
  {
    Icon: MessageSquare,
    path: Routes.HealthDataSurvey.path,
    labelKey: 'dashboard.create-health-interview',
  },
  {
    Icon: Dna,
    path: Routes.GeneticProfile.path,
    labelKey: 'dashboard.quick-actions-genetic-profile',
  },
  {
    Icon: Share2,
    path: Routes.SharedAccessLinks.path,
    labelKey: 'dashboard.share-with-doctor',
  },
  {
    Icon: ScanFace,
    path: Routes.SkinMoles.path,
    labelKey: 'dashboard.check-your-skin-mole',
  },
  {
    Icon: Calculator,
    path: Routes.HealthCalculators.path,
    labelKey: 'dashboard.quick-actions-calculators',
  },
  {
    Icon: Sparkles,
    path: null,
    labelKey: 'dashboard.copy-profile-for-ai',
    premium: true,
    action: 'personaAiCopy',
  },
];

function isInProgressStatus(status) {
  return String(status ?? '').toUpperCase() === 'IN_PROGRESS';
}

async function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  document.execCommand('copy');
  document.body.removeChild(ta);
}

export function QuickActionsCard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [personaBusy, setPersonaBusy] = useState(false);

  const {
    data: snapshots = [],
    isLoading: personaListLoading,
    isFetching: personaListFetching,
    isError: personaListError,
    refetch: refetchPersonaSnapshots,
  } = useFetchPersonaSnapshotsQuery();

  const [fetchPersonaSnapshot] = useLazyFetchPersonaSnapshotQuery();

  const resolveCurrentSnapshot = useCallback((rows) => {
    const sorted = [...rows].sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return tb - ta;
    });
    const active = sorted.find((s) => s.isActive);
    return active ?? sorted[0] ?? null;
  }, []);

  const personaAiPath = Routes.MedicalPersonaAi.path;

  const handlePersonaAiQuickAction = useCallback(async () => {
    setPersonaBusy(true);
    try {
      let rows = snapshots;
      if (personaListLoading || personaListFetching) {
        const refetchResult = await refetchPersonaSnapshots();
        if (refetchResult.error) {
          toast.error(t('medical-persona.load-error'));
          navigate(personaAiPath);
          return;
        }
        rows = refetchResult.data ?? [];
      } else if (personaListError) {
        toast.error(t('medical-persona.load-error'));
        navigate(personaAiPath);
        return;
      }

      const current = resolveCurrentSnapshot(rows);
      if (!current) {
        navigate(personaAiPath);
        return;
      }

      if (isInProgressStatus(current.processStatus)) {
        navigate(personaAiPath);
        return;
      }

      let md = typeof current.markdown === 'string' ? current.markdown.trim() : '';
      if (!md) {
        try {
          const detail = await fetchPersonaSnapshot(current.uuid).unwrap();
          md = typeof detail?.markdown === 'string' ? detail.markdown.trim() : '';
        } catch {
          toast.error(t('medical-persona.load-error'));
          navigate(personaAiPath);
          return;
        }
      }

      if (!md) {
        navigate(personaAiPath);
        return;
      }

      await copyToClipboard(md);
      toast.success(t('medical-persona.toast-copied'));
    } catch {
      toast.error(t('medical-persona.toast-copy-failed'));
    } finally {
      setPersonaBusy(false);
    }
  }, [
    snapshots,
    personaListLoading,
    personaListFetching,
    personaListError,
    refetchPersonaSnapshots,
    resolveCurrentSnapshot,
    fetchPersonaSnapshot,
    navigate,
    personaAiPath,
    t,
  ]);

  return (
    <Card className='quick-actions-bootstrap-card d-flex h-100 w-100 flex-column border-0 shadow'>
      <Card.Header className='bg-white border-bottom py-3 pb-2'>
        <div
          className='mb-0 d-flex align-items-center gap-2 fw-semibold text-dark'
          style={{ fontSize: '1rem' }}
        >
          <Zap
            className='flex-shrink-0'
            style={{ width: '1.25rem', height: '1.25rem', color: 'var(--primary)' }}
            strokeWidth={2}
          />
          {t('dashboard.quick-actions')}
        </div>
      </Card.Header>
      <Card.Body className='pt-3'>
        <div className='row g-3'>
          {ACTIONS.map(({ Icon, path, labelKey, premium, action }) => {
            const isStatic = !path && !premium;
            const tileClass = [
              'quick-action-bootstrap-tile',
              'd-flex',
              'w-100',
              'flex-column',
              'align-items-center',
              'justify-content-center',
              'gap-2',
              'text-center',
              premium && 'quick-action-bootstrap-tile--premium',
              isStatic && 'quick-action-bootstrap-tile--static',
            ]
              .filter(Boolean)
              .join(' ');

            const onTileClick = () => {
              if (action === 'personaAiCopy') {
                handlePersonaAiQuickAction();
              } else if (path) {
                navigate(path);
              }
            };

            const busy = action === 'personaAiCopy' && personaBusy;

            return (
              <div key={labelKey} className='col-6'>
                <button type='button' className={tileClass} onClick={onTileClick} disabled={busy}>
                  {busy ? (
                    <Loader2
                      className='flex-shrink-0 quick-action-tile-icon quick-action-premium-icon medical-persona-icon--spin'
                      strokeWidth={2}
                      aria-hidden
                    />
                  ) : (
                    <Icon
                      className={`flex-shrink-0 quick-action-tile-icon${
                        premium ? ' quick-action-premium-icon' : ''
                      }`}
                      style={premium ? undefined : { color: 'var(--primary)' }}
                      strokeWidth={2}
                    />
                  )}
                  <span
                    className='fw-medium text-dark'
                    style={{ fontSize: '0.75rem', lineHeight: 1.3 }}
                  >
                    {t(labelKey)}
                  </span>
                </button>
              </div>
            );
          })}
        </div>
      </Card.Body>
    </Card>
  );
}
